import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { DepartmentResource } from '@/types/resources';
import { api } from '@/utils/api';

import DepartmentFormModal from './DepartmentFormModal';

interface Props {
  department: DepartmentResource;
}

const DepartmentsTableRow: FC<Props> = ({ department }) => {
  const [showEditModal, setShowEditModal] = useState(false);

  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this department?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/departments/${department.id}`,
      );

      await queryClient.invalidateQueries(['departments_and_units'], {
        exact: false,
      });

      alerts.success('Department was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <>
      <tr>
        <Td>{department.name}</Td>
        <Td>{department.short}</Td>
        <Td>
          {department.companies.length
            ? department.companies.map((c) => c.name).join(', ')
            : '-'}
        </Td>
        <Td>{department.hide_from_dropdowns ? 'Yes' : 'No'}</Td>
        <Td $actionsCol>
          <ActionsWrap>
            <ActionsDropdown>
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowEditModal(true)}
              >
                <span>
                  <PencilIcon />
                </span>
                Edit
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </ActionsDropdown>
          </ActionsWrap>
        </Td>
      </tr>
      <AnimatePresence>
        {showEditModal && (
          <DepartmentFormModal
            department={department}
            onClose={() => {
              setShowEditModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default DepartmentsTableRow;
